/* Bilingual + Theme system — single source of truth.
 * Switching language flips dir (rtl/ltr); switching theme flips palette.
 * Persisted to localStorage. Applied app-wide via I18nThemeProvider.
 */

export const TRANSLATIONS = {
  ar: {
    // Brand
    "office.name": "وكالة التأشيرات للسفر والسياحة",
    "app.title": "منصة تشغيل مكتب التأشيرات",
    "app.checking_session": "جارٍ التحقق من الجلسة…",

    // Login
    "login.subtitle": "تسجيل الدخول للنظام الداخلي",
    "login.username": "اسم المستخدم",
    "login.username_placeholder": "مثل: stas",
    "login.password": "كلمة المرور",
    "login.submit": "دخول",
    "login.submitting": "جارٍ الدخول…",

    // Dashboard
    "dashboard.logout": "خروج",
    "dashboard.welcome": "مرحباً، {name}",
    "dashboard.welcome_sub": "لوحة تحكم مبدئية — التفاصيل والمزايا ستُضاف لاحقاً حسب طلبك.",
    "dashboard.meta.role": "الدور",
    "dashboard.meta.username": "المعرّف",
    "dashboard.card.applications": "المعاملات المتاحة لك",
    "dashboard.card.payments": "المدفوعات",
    "dashboard.card.payments_review": "المدفوعات (مراجعة)",
    "dashboard.card.role": "الدور",
    "dashboard.card.permissions": "الصلاحيات الخاصة",
    "dashboard.permissions.root": "تجاوز كامل",
    "dashboard.permissions.scoped": "حسب الدور",
    "dashboard.phase_title": "المرحلة الحالية: البنية الأساسية",
    "dashboard.phase.1": "قاعدة بيانات PostgreSQL مع كل الجداول",
    "dashboard.phase.2": "نظام أدوار وصلاحيات ديناميكي بالكامل",
    "dashboard.phase.3": "Workflow Engine قابل للتعديل من STAS",
    "dashboard.phase.4": "المعاملات + الأعضاء + المرفقات + المدفوعات",
    "dashboard.phase.5": "Audit Logs لكل عملية حساسة",
    "dashboard.phase.6": "زر تبديل المستخدم (بيئة التطوير فقط)",
    "dashboard.phase_next": "في انتظار توجيهك للمرحلة التالية (واجهات تفصيلية، رفع ملفات، بوابة العميل…).",

    // Dev switcher
    "dev.switch_button": "تبديل المستخدم (DEV)",
    "dev.pick_user": "اختر مستخدماً",
    "dev.switch_tooltip": "بيئة التطوير فقط — يتم تسجيل كل تبديل في سجل التتبع",

    // Toolbar
    "toolbar.language": "اللغة",
    "toolbar.theme": "الوضع",
    "toolbar.theme.dark": "داكن",
    "toolbar.theme.light": "فاتح",
    "toolbar.lang.ar": "العربية",
    "toolbar.lang.en": "English",
  },

  en: {
    "office.name": "Visa Travel Agency",
    "app.title": "Visa Office Operations Platform",
    "app.checking_session": "Verifying session…",

    "login.subtitle": "Sign in to the internal system",
    "login.username": "Username",
    "login.username_placeholder": "e.g. stas",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.submitting": "Signing in…",

    "dashboard.logout": "Logout",
    "dashboard.welcome": "Welcome, {name}",
    "dashboard.welcome_sub": "Preliminary dashboard — details and features will be added per your direction.",
    "dashboard.meta.role": "Role",
    "dashboard.meta.username": "User",
    "dashboard.card.applications": "Applications available",
    "dashboard.card.payments": "Payments",
    "dashboard.card.payments_review": "Payments (review)",
    "dashboard.card.role": "Role",
    "dashboard.card.permissions": "Permissions",
    "dashboard.permissions.root": "Full override",
    "dashboard.permissions.scoped": "Per role",
    "dashboard.phase_title": "Current phase: Base Architecture",
    "dashboard.phase.1": "PostgreSQL database with all tables",
    "dashboard.phase.2": "Fully dynamic roles & permissions system",
    "dashboard.phase.3": "Workflow Engine editable by STAS",
    "dashboard.phase.4": "Applications + members + attachments + payments",
    "dashboard.phase.5": "Audit logs for every sensitive action",
    "dashboard.phase.6": "User switcher (development only)",
    "dashboard.phase_next": "Awaiting your direction for the next phase (detailed UI, file upload, client portal…).",

    "dev.switch_button": "Switch user (DEV)",
    "dev.pick_user": "Pick a user",
    "dev.switch_tooltip": "Development only — every switch is recorded in audit logs",

    "toolbar.language": "Language",
    "toolbar.theme": "Theme",
    "toolbar.theme.dark": "Dark",
    "toolbar.theme.light": "Light",
    "toolbar.lang.ar": "العربية",
    "toolbar.lang.en": "English",
  },
};

export const THEMES = {
  dark: {
    name: "dark",
    bg: "#0a0a0a",
    bgGradient: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, #2a2418 100%)",
    surface: "#141414",
    surfaceAlt: "#1a1a1a",
    surfaceCard: "linear-gradient(135deg, #161616 0%, #1d1810 100%)",
    text: "#f3f3f3",
    textSoft: "#cfcfcf",
    textMuted: "#a8a8a8",
    textFaint: "#666",
    border: "#2a2a2a",
    borderSoft: "#1f1f1f",
    borderAccent: "rgba(212,175,55,0.25)",
    inputBg: "#0f0f0f",
    inputBorder: "#333",
    gold: "#d4af37",
    goldGradient: "linear-gradient(135deg, #d4af37 0%, #b8902a 100%)",
    goldOnDark: "#d4af37",
    errorBg: "rgba(220,38,38,0.12)",
    errorBorder: "rgba(220,38,38,0.35)",
    errorText: "#fca5a5",
    headerBg: "rgba(15,15,15,0.95)",
    cardShadow: "0 20px 60px rgba(0,0,0,0.4)",
  },
  light: {
    name: "light",
    bg: "#f5f5f3",
    bgGradient: "linear-gradient(135deg, #f7f6f1 0%, #ecebe6 60%, #e8e1c8 100%)",
    surface: "#ffffff",
    surfaceAlt: "#fafaf9",
    surfaceCard: "linear-gradient(135deg, #ffffff 0%, #fbf8ed 100%)",
    text: "#161616",
    textSoft: "#2a2a2a",
    textMuted: "#5a5a5a",
    textFaint: "#8a8a8a",
    border: "#e3e1dc",
    borderSoft: "#ececea",
    borderAccent: "rgba(184,144,42,0.45)",
    inputBg: "#ffffff",
    inputBorder: "#d4d2cc",
    gold: "#a67c1a",
    goldGradient: "linear-gradient(135deg, #c79a26 0%, #a67c1a 100%)",
    goldOnDark: "#a67c1a",
    errorBg: "rgba(220,38,38,0.08)",
    errorBorder: "rgba(220,38,38,0.45)",
    errorText: "#b91c1c",
    headerBg: "rgba(255,255,255,0.92)",
    cardShadow: "0 20px 50px rgba(40,30,10,0.10)",
  },
};

export const DIRECTIONS = { ar: "rtl", en: "ltr", ur: "rtl" };
